import { BoolQueryParam } from '../../../common/api/Query'
import { v4 as uuidv4 } from 'uuid'
import { UserGender } from '../enums/UserGender'
import { LangType } from '../../content/enums/LangType'

export interface DietData {
  types: string[]
  texture: string
}

export function emptyDiet(): DietData {
  return { types: [], texture: '' }
}

export interface UserDTO {
  id: string
  firstName: string
  lastName: string
  gender: UserGender
  email: string
  dob: Date
  language: LangType
  historyRecordNumber: number
  diagnosisSummary: string
  diagnosis: string
  isPatient: boolean
  isActive: boolean
  assignedID: string
  roles: string[]
  patologies: string[]
  notificationsToken: string
  diet: DietData
  permissions?: string[]
  otherPatology?: string
}

export function emptyUserDTO() {
  return {
    id: uuidv4(),
    firstName: '',
    lastName: '',
    gender: UserGender.Female,
    email: '',
    dob: new Date(),
    language: LangType.Spanish,
    historyRecordNumber: 0,
    diagnosisSummary: '',
    diagnosis: '',
    isPatient: true,
    isActive: true,
    assignedID: '',
    roles: [],
    patologies: [],
    notificationsToken: '',
    diet: emptyDiet(),
    permissions: [],
    otherPatology: '',
  }
}

export class User {
  private readonly _id: string
  private readonly _isPatient: boolean

  private _firstName: string
  private _lastName: string
  private _gender: UserGender
  private _email: string
  private _dob: Date
  private _language: LangType
  private _historyRecordNumber: number
  private _diagnosisSummary: string
  private _diagnosis: string
  private _isActive: boolean
  private _assignedID: string
  private readonly _roles: string[]
  private readonly _patologies: string[]
  private _notificationsToken: string
  private _diet: DietData
  private _permissions?: string[]
  private _otherPatology?: string

  constructor(p: UserDTO) {
    this._id = p.id
    this._firstName = p.firstName
    this._lastName = p.lastName
    this._gender = p.gender
    this._email = p.email
    this._dob = p.dob
    this._language = p.language
    this._historyRecordNumber = p.historyRecordNumber
    this._diagnosisSummary = p.diagnosisSummary
    this._diagnosis = p.diagnosis
    this._isPatient = p.isPatient
    this._isActive = p.isActive
    this._assignedID = p.assignedID
    this._roles = p.roles
    this._patologies = p.patologies
    this._notificationsToken = p.notificationsToken
    this._diet = p.diet
    this._permissions = p.permissions
    this._otherPatology = p.otherPatology
  }

  get roles(): string[] {
    return this._roles
  }

  get patologies(): string[] {
    return this._patologies
  }

  get id(): string {
    return this._id
  }

  get isPatient(): boolean {
    return this._isPatient
  }

  get firstName(): string {
    return this._firstName
  }

  set firstName(value: string) {
    this._firstName = value
  }

  get lastName(): string {
    return this._lastName
  }

  set lastName(value: string) {
    this._lastName = value
  }

  get gender(): UserGender {
    return this._gender
  }

  set gender(value: UserGender) {
    this._gender = value
  }

  get email(): string {
    return this._email
  }

  set email(value: string) {
    this._email = value
  }

  get dob(): Date {
    return this._dob
  }

  set dob(value: Date) {
    this._dob = value
  }

  get language(): LangType {
    return this._language
  }

  set language(value: LangType) {
    this._language = value
  }

  get historyRecordNumber(): number {
    return this._historyRecordNumber
  }

  set historyRecordNumber(value: number) {
    this._historyRecordNumber = value
  }

  get diagnosisSummary(): string {
    return this._diagnosisSummary
  }

  set diagnosisSummary(value: string) {
    this._diagnosisSummary = value
  }

  get diagnosis(): string {
    return this._diagnosis
  }

  set diagnosis(value: string) {
    this._diagnosis = value
  }

  get isActive(): boolean {
    return this._isActive
  }

  set isActive(value: boolean) {
    this._isActive = value
  }

  get assignedID(): string {
    return this._assignedID
  }

  set assignedID(value: string) {
    this._assignedID = value
  }

  get notificationsToken(): string {
    return this._notificationsToken
  }

  set notificationsToken(value: string) {
    this._notificationsToken = value
  }

  get diet(): DietData {
    return this._diet
  }

  set diet(value: DietData) {
    this._diet = value
  }

  get permissions(): string[] | undefined {
    return this._permissions
  }

  set permissions(value: string[] | undefined) {
    this._permissions = value
  }

  get otherPatology(): string | undefined {
    return this._otherPatology
  }

  set otherPatology(value: string | undefined) {
    this._otherPatology = value
  }

  toDTO(): UserDTO {
    return {
      id: this.id,
      firstName: this.firstName,
      lastName: this.lastName,
      gender: this.gender,
      email: this.email,
      dob: this.dob,
      language: this.language,
      historyRecordNumber: this.historyRecordNumber,
      diagnosisSummary: this.diagnosisSummary,
      diagnosis: this.diagnosis,
      isPatient: this.isPatient,
      isActive: this.isActive,
      assignedID: this.assignedID,
      roles: this.roles,
      patologies: this.patologies,
      notificationsToken: this.notificationsToken,
      diet: this.diet,
      permissions: this.permissions,
      otherPatology: this.otherPatology,
    }
  }
}

export interface UserQuery {
  id: string
  ids: string[]
  isPatient: BoolQueryParam
  firstName: string
  lastName: string
  historyRecordNumber: number
  assignedID: string
  isActive: BoolQueryParam
}

export function toModel(d: UserDTO): User {
  return new User(d)
}

export function fromModel(d: User): UserDTO {
  return {
    id: d.id,
    firstName: d.firstName,
    lastName: d.lastName,
    gender: d.gender,
    email: d.email,
    dob: d.dob,
    language: d.language,
    historyRecordNumber: d.historyRecordNumber,
    diagnosisSummary: d.diagnosisSummary,
    diagnosis: d.diagnosis,
    isPatient: d.isPatient,
    isActive: d.isActive,
    assignedID: d.assignedID,
    roles: d.roles,
    patologies: d.patologies,
    notificationsToken: d.notificationsToken,
    diet: d.diet,
    permissions: d.permissions,
    otherPatology: d.otherPatology,
  }
}
